import { FieldPath, UseFormSetError } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import { createPledge } from 'api/pledges';
import { PledgeFormValue } from 'assets/js/pages/pledges/usePledgeForm';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateToServerDateTime } from 'helpers/dates';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Pledge } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const useCreatePledge = ({
    setError,
    onSuccess,
    onError,
}: {
    setError: UseFormSetError<PledgeFormValue>;
    onSuccess?: (data: Pledge) => void;
    onError?: (e: GivebutterAxiosError) => void;
}) => {
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const toast = useToast();

    return useMutation({
        mutationFn: ({
            firstName,
            lastName,
            emails,
            phones,
            contactType,
            companyName,
            ...data
        }: PledgeFormValue) => {
            const contactId = parseInt(data.selectedContact?.[0]?.id);
            return createPledge(accountId, {
                ...data,
                contact:
                    contactId === -1
                        ? {
                              first_name: firstName,
                              last_name: lastName,
                              emails,
                              phones: phones[0].value ? phones : [],
                              company_name: companyName,
                              type: contactType,
                          }
                        : undefined,
                contact_id: contactId !== -1 ? contactId : undefined,
                campaign_id: data.campaign?.[0]?.id ? parseInt(data.campaign[0].id) : undefined,
                fund_id: data.fund?.[0]?.id ? parseInt(data.fund[0].id) : null,
                amount: unmaskNumber(data.amount),
                first_installment_date: dateToServerDateTime(
                    data.first_installment_date.endOf('day'),
                ),
                fulfilled_at: dateToServerDateTime(data.fulfilled_at.endOf('day')),
                number_of_installments: data.installments.length,
                installments: data.installments.map(({ date, amount }) => ({
                    date: dateToServerDateTime(date.endOf('day')),
                    amount: unmaskNumber(amount),
                })),
            });
        },
        onSuccess: (data: Pledge) => {
            toast.success('Pledge created successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pledges(accountId),
            });
            onSuccess?.(data);
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data.errors;
            toast.error(e.response?.data?.message ?? 'An unknown error occurred.');
            if (errors) {
                Object.keys(errors).forEach(errorKey => {
                    let key =
                        {
                            campaign_id: 'campaign',
                            fund_id: 'fund',
                            contact_id: 'selectedContact',
                        }[errorKey] ?? errorKey;

                    if (key.startsWith('contact.')) {
                        key = key.replace('contact.', '');
                    }

                    const error = errors[errorKey][0];

                    const message = error.replace(
                        /installments\.\d+\.amount/g,
                        'installment amount',
                    );

                    setError(key as FieldPath<PledgeFormValue>, {
                        message,
                    });
                });
            }
            onError?.(e);
        },
    });
};

export { useCreatePledge };
